import React, { useState, useEffect, useContext, useMemo } from 'react';
import httpService, { storedAccessToken } from '../../services/http';

type Props = {
  children: React.ReactNode;
};

export type Tenant = { id: string; name: string; slug: string };
export type Organisation = { id: string; name: string };
export type Team = { id: number; name: string };

export type UserProfile = {
  first_name: string;
  last_name: string;
  email: string;
  id: number;
  hubspotintegration: number | null;
  company: string;
  is_staff: boolean;
  is_platform_staff: boolean;
  /** superuser or platform staff; not sent by backends older than the field */
  has_tenant_wide_access?: boolean;
  /** null for superusers, who pick the tenant at login: their tenant is the one from the frontend config */
  tenant: Tenant | null;
  /** null for free-tier users */
  organisation: Organisation | null;
  team: Team | null;
  permissions: string[];
};

type contextType = {
  email: string;
  firstName: string;
  lastName: string;
  teamId: number;
  teamName: string;
  isStaff: boolean;
  /** superuser or platform staff: sees and edits everything inside the tenant */
  hasTenantWideAccess: boolean;
  tenant: Tenant | null;
  organisation: Organisation | null;
  /** creating systems, components, component instances and EnergyPlus models requires an organisation */
  canCreateResources: boolean;
  /** opening the system interface and exporting the Modelica model of a simulation */
  canEditSystems: boolean;
  setUserData: any;
  isDemo: boolean;
  setIsDemo: any;
  simName: string;
  setSimName: any;
  permissions: string[];
  user: UserProfile | any;
};

export const UserDataContext: React.Context<contextType | undefined> = React.createContext<contextType | undefined>(
  undefined,
);

const UserDataProvider = ({ children }: Props) => {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [teamId, setTeamId] = useState(-1);
  const [teamName, setTeamName] = useState('');
  const [isDemo, setIsDemo] = useState(false);
  const [simName, setSimName] = useState('New Simulation');
  const [user, setUser] = useState({});
  const [isStaff, setIsStaff] = useState(false);
  const [hasTenantWideAccess, setHasTenantWideAccess] = useState(false);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [organisation, setOrganisation] = useState<Organisation | null>(null);
  const [permissions, setPermissions] = useState<string[]>([]);

  /**
   * @description is called to set user data
   */
  const setUserData = () => {
    httpService
      .get('user/', {}, 'json', true)
      .then((response) => {
        const profile: UserProfile = response.data[0];
        setUser(profile);
        setEmail(profile.email);
        setFirstName(profile.first_name);
        setLastName(profile.last_name);
        setIsStaff(profile.is_staff);
        setHasTenantWideAccess(
          profile.has_tenant_wide_access ??
            // backends older than the field: superusers are the only accounts without a tenant
            (Boolean(profile.is_platform_staff) || profile.tenant === null),
        );
        setTenant(profile.tenant ?? null);
        setOrganisation(profile.organisation ?? null);
        setPermissions(profile.permissions ?? []);
        sessionStorage.setItem('userId', String(profile.id));

        if (profile.last_name === 'Demo' && profile.first_name === 'User') {
          setIsDemo(true);
        }

        if (profile.team?.id) {
          setTeamId(profile.team.id);
          setTeamName(profile.team.name);
        } else {
          setTeamId(-1);
          setTeamName('');
        }

        return true;
      })
      .catch((error) => error);
  };

  useEffect(() => {
    if (storedAccessToken()) {
      setUserData();
    }
  }, []);

  const contextValue = useMemo(() => {
    return {
      email,
      firstName,
      lastName,
      teamId,
      teamName,
      isStaff,
      hasTenantWideAccess,
      tenant,
      organisation,
      canCreateResources: hasTenantWideAccess || organisation !== null,
      canEditSystems: hasTenantWideAccess || permissions.includes('simulation.change_fmu'),
      setUserData,
      isDemo,
      setIsDemo,
      simName,
      setSimName,
      permissions,
      user,
    };
  }, [
    email,
    firstName,
    lastName,
    teamId,
    teamName,
    isStaff,
    hasTenantWideAccess,
    tenant,
    organisation,
    isDemo,
    simName,
    permissions,
    user,
  ]);
  return <UserDataContext.Provider value={contextValue}>{children}</UserDataContext.Provider>;
};

export const useUserDataContext = (): contextType => {
  const context = useContext(UserDataContext);

  if (context === undefined) {
    throw new Error('useUserDataContext must be used within a UserDataProvider');
  }

  return context;
};
export default UserDataProvider;
